
import { MessageSquare, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

interface RequestBudgetCardProps {
  budgetMin: number;
  budgetMax: number;
  expiresAt: string;
}

const RequestBudgetCard = ({ budgetMin, budgetMax, expiresAt }: RequestBudgetCardProps) => {
  const [messageText, setMessageText] = useState("");
  const { toast } = useToast();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const handleSendResponse = () => {
    if (!messageText.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message",
        variant: "destructive",
      });
      return;
    }
    
    toast({
      title: "Response Sent",
      description: "Your response has been sent to the buyer",
    });
    
    setMessageText("");
  };

  return (
    <Card className="sticky top-4">
      <CardHeader>
        <CardTitle className="text-2xl font-bold">
          ${budgetMin} - ${budgetMax}
        </CardTitle>
        <CardDescription>Buyer's budget range</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-gray-500" />
          <span>Expires on {formatDate(expiresAt)}</span>
        </div>
        
        <Separator />
        
        <Dialog>
          <DialogTrigger asChild>
            <Button className="w-full">
              <MessageSquare className="h-4 w-4 mr-2" />
              Respond to Request
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Response to Request</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Textarea 
                placeholder="Describe your offer and how you can meet their requirements..."
                className="min-h-[150px]"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
            </div>
            <Button onClick={handleSendResponse}>Send Response</Button>
          </DialogContent>
        </Dialog>
        
        <Button variant="outline" className="w-full" asChild>
          <Link to="/buyer-requests">View All Requests</Link>
        </Button>
      </CardContent>
      <CardFooter className="flex flex-col items-start">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="h-4 w-4" />
          Respond before expiration to ensure consideration
        </div>
      </CardFooter>
    </Card>
  );
};

export default RequestBudgetCard;
